import { useRef, useState } from "react";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";

import { useMediaQuery } from "@/hooks";
import type { EmbarcacaoDetailSection } from "./embarcacao-detail-section";
import { EMBARCACAO_DETAIL_SECTIONS } from "./sections";
import styles from "./embarcacao-detail-dialog.module.scss";

export const EMBARCACAO_DETAIL_TITLE = "Detalhes da Embarcação";
export const EMBARCACAO_DETAIL_MENU_LABEL = "Seções do detalhamento";
export const EMBARCACAO_DETAIL_SECTION_SELECT_PREFIX = "Seção:";

/**
 * Ponto de quebra do modo mobile, o mesmo do layout autenticado e da Tela
 * Inicial. Abaixo dele o menu lateral de seções dá lugar a um seletor no topo
 * do corpo: o corpo rolável em visualização vertical única ocupa a largura toda.
 */
export const EMBARCACAO_DETAIL_MOBILE_QUERY = "(max-width: 768px)";

export const EMBARCACAO_DETAIL_EXPORT_LABEL = "Exportar";

/**
 * "Exportar" é só o ponto de entrada previsto no protótipo: a exportação do
 * detalhamento é história própria, então o botão aparece no lugar desenhado sem
 * executar nada nesta entrega — como o "Exportar Visualização" da listagem.
 */
export const EMBARCACAO_DETAIL_EXPORT_UNAVAILABLE_TITLE = "Disponível em uma próxima entrega.";

/**
 * Folga entre o topo do corpo e o início da seção considerada ativa: sem ela a
 * seção só seria marcada depois de o seu topo ultrapassar a borda do corpo.
 */
const ACTIVE_SECTION_TOLERANCE = 24;

interface EmbarcacaoDetailDialogProps {
  /** Identificador técnico da embarcação acionada na lupa da listagem (RN004). */
  readonly embarcacaoId: string;
  /** Única ação de fechamento; devolve o foco à lupa de origem (AC1). */
  readonly onClose: () => void;
  /** Fluxo de reautenticação acionado quando a sessão expira (401). */
  readonly onReauthenticate: () => void;
  /**
   * Seções do detalhamento. O padrão é o registro único do módulo (AC2); o
   * parâmetro existe para que a casca possa ser exercitada com outras listas.
   */
  readonly sections?: EmbarcacaoDetailSection[];
}

/**
 * Modal Detalhes da Embarcação: casca de detalhamento somente leitura,
 * sobreposta à listagem (AC1).
 *
 * A casca não conhece nenhuma seção: monta o menu lateral e o corpo rolável a
 * partir do registro de seções, de modo que uma seção nova entre no
 * detalhamento sem tocar aqui (AC2). Nenhuma escrita é disparada na abertura
 * ou no fechamento — a consulta de cada seção é a única chamada feita (RN003).
 */
export function EmbarcacaoDetailDialog({
  embarcacaoId,
  onClose,
  onReauthenticate,
  sections = EMBARCACAO_DETAIL_SECTIONS,
}: EmbarcacaoDetailDialogProps) {
  const bodyRef = useRef<HTMLDivElement>(null);
  // A primeira seção registrada é a ativa quando o modal abre (AC2).
  const [activeId, setActiveId] = useState(() => sections[0]?.id ?? "");
  // No mobile o menu lateral dá lugar a um seletor de seções no topo do corpo.
  const isMobile = useMediaQuery(EMBARCACAO_DETAIL_MOBILE_QUERY);

  /** Direciona o corpo do modal para a seção pedida — rolagem, sem troca de tela. */
  function handleSelect(id: string) {
    setActiveId(id);

    const body = bodyRef.current;
    const section = body?.querySelector<HTMLElement>(`[data-section-id="${id}"]`);
    if (body && section) body.scrollTop = section.offsetTop;
  }

  /** Rolar o corpo também marca no menu a seção que está em leitura. */
  function handleScroll() {
    const body = bodyRef.current;
    if (!body) return;

    const elements = Array.from(body.querySelectorAll<HTMLElement>("[data-section-id]"));
    const limit = body.scrollTop + ACTIVE_SECTION_TOLERANCE;
    const current = elements.filter((element) => element.offsetTop <= limit).at(-1) ?? elements[0];

    if (current?.dataset.sectionId) setActiveId(current.dataset.sectionId);
  }

  return (
    <Dialog
      className={styles.dialog}
      maskClassName={styles.mask}
      contentClassName={styles.content}
      header={EMBARCACAO_DETAIL_TITLE}
      visible
      modal
      draggable={false}
      resizable={false}
      dismissableMask
      ariaCloseIconLabel="Fechar"
      onHide={onClose}
      breakpoints={{ "80rem": "95vw", "40rem": "100vw" }}
      data-testid="embarcacao-detail-dialog"
    >
      <div className={styles.layout}>
        {/* Menu lateral de seções: no mobile dá lugar ao seletor no topo do corpo. */}
        {isMobile ? null : (
          <nav
            className={styles.menu}
            aria-label={EMBARCACAO_DETAIL_MENU_LABEL}
            data-testid="embarcacao-detail-menu"
          >
            <ul className={styles.menuList}>
              {sections.map((section) => {
                const isActive = section.id === activeId;

                return (
                  <li key={section.id}>
                    <button
                      type="button"
                      className={`${styles.menuItem} ${isActive ? styles.menuItemActive : ""}`}
                      aria-current={isActive ? "true" : undefined}
                      onClick={() => handleSelect(section.id)}
                      data-testid={`embarcacao-detail-menu-${section.id}`}
                    >
                      {section.title}
                    </button>
                  </li>
                );
              })}
            </ul>
          </nav>
        )}

        {/* Visualização vertical única: todas as seções vivem no mesmo corpo rolável. */}
        <div
          className={styles.body}
          ref={bodyRef}
          onScroll={handleScroll}
          data-testid="embarcacao-detail-body"
        >
          {/* Topo do corpo: no mobile, o seletor de seções à esquerda do "Exportar". */}
          <div className={styles.bodyActions}>
            {isMobile ? (
              <Dropdown
                className={styles.sectionSelect}
                panelClassName={styles.sectionSelectPanel}
                value={activeId}
                options={sections.map((section) => ({
                  label: section.title,
                  value: section.id,
                }))}
                onChange={(event) => handleSelect(event.value as string)}
                valueTemplate={(option) => (
                  <span className={styles.sectionSelectValue}>
                    <span className={styles.sectionSelectPrefix}>
                      {EMBARCACAO_DETAIL_SECTION_SELECT_PREFIX}
                    </span>
                    {option?.label}
                  </span>
                )}
                aria-label={EMBARCACAO_DETAIL_MENU_LABEL}
                data-testid="embarcacao-detail-section-select"
              />
            ) : null}
            <Button
              type="button"
              className={styles.exportButton}
              label={EMBARCACAO_DETAIL_EXPORT_LABEL}
              severity="secondary"
              title={EMBARCACAO_DETAIL_EXPORT_UNAVAILABLE_TITLE}
              disabled
              data-testid="embarcacao-detail-export"
            />
          </div>

          {sections.map(({ id, Component }) => (
            <Component key={id} embarcacaoId={embarcacaoId} onReauthenticate={onReauthenticate} />
          ))}
        </div>
      </div>
    </Dialog>
  );
}
